<template>
  <div>
    <v-form ref="form" @submit.prevent="handleSubmit" v-model="valid">
      <v-container>
        <v-row>
          <v-col cols="12">
            <date-picker
              label="Date"
              :defaultNowOnNull="false"
              :show-hint="false"
              :show-icon="false"
              v-model="record.date"
              :outlined="false"
              :rules="[rules.required, rules.futureDate]"
              clearable
            />
          </v-col>
          <v-col cols="12">
            <v-select
              :items="myCenters"
              label="Location"
              v-model="record.locationId"
              item-value="id"
              item-text="name"
            ></v-select>
          </v-col>
          <v-col cols="12">
            <v-text-field
              label="Reason"
              v-model="record.reason"
              :rules="[rules.required]"
              required
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="d-flex justify-space-between">
          <v-btn
            type="submit"
            :disabled="!valid || isPastRecord || loading"
            :loading="loading"
          >
            {{ isEditing ? "Update" : "Create" }}
          </v-btn>
          <v-btn
            v-if="isEditing && !isPastRecord"
            @click="deleteRecord"
            color="red"
            :loading="loading"
          >
            Delete
          </v-btn>
        </div>
      </v-container>
    </v-form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import dialogMixin from "./dialogMixin";
import { EventBus } from "@/EventBus.js";

export default {
  mixins: [dialogMixin],
  props: {
    id: {
      type: String,
      default: null,
    },
  },
  watch: {
    id(newId, oldId) {
      if (newId !== oldId) {
        if (this.isEditing) {
          this.fetchRecord();
        } else {
          this.resetForm();
        }
      }
    },
  },
  data() {
    return {
      valid: true,
      loading: false,
      record: {
        date: "",
        locationId: null,
        reason: "",
        isGlobal: false,
      },
      rules: {
        required: (value) => !!value || "Required field.",
        futureDate: (value) =>
          new Date(value) >= new Date() || "Date cannot be in the past.",
      },
    };
  },
  computed: {
    ...mapGetters(["myCenters"]),

    isEditing() {
      return this.id !== null;
    },

    isPastRecord() {
      return new Date(this.record.date) < new Date();
    },
  },
  created() {
    if (this.isEditing) {
      this.fetchRecord();
    }
  },
  methods: {
    ...mapActions(["addErrorMessage", "addSuccessMessage", "clearMessage"]),
    resetForm() {
      this.record = {
        date: "",
        locationId: null,
        reason: "",
        isGlobal: false,
      };
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
    fetchRecord() {
      this.$axios
        .get(`/cancellation-master/${this.id}`)
        .then((response) => {
          this.record = response.data;
        })
        .catch((error) => {
          console.error("An error occurred while fetching the record:", error);
        });
    },
    deleteRecord() {
      if (confirm("Are you sure you want to delete this record?")) {
        this.loading = true; // Start loading
        this.$axios
          .delete(`/cancellation-master/${this.id}`)
          .then(() => {
            this.addSuccessMessage("Record Deleted");
            this.onClose();
            EventBus.$emit("reload-chart");
            EventBus.$emit("reload-dialog", this.parentID);
          })
          .catch((error) => {
            this.addErrorMessage("An error occurred while deleting the record");
            console.error(
              "An error occurred while deleting the record:",
              error
            );
          })
          .finally(() => {
            this.loading = false; // Stop loading
          });
      }
    },
    handleSubmit() {
      if (this.$refs.form.validate()) {
        this.loading = true; // Start loading

        // Set isGlobal to true if locationId is null
        if (!this.record.locationId) {
          this.record.isGlobal = true;
        } else {
          this.record.isGlobal = false;
        }

        const method = this.isEditing ? "PUT" : "POST";
        const url = this.isEditing
          ? `/cancellation-master/${this.id}`
          : "/cancellation-master";

        this.$axios({
          method,
          url,
          data: this.record,
        })
          .then(() => {
            this.addSuccessMessage(
              method == "PUT" ? "Record Updated" : "Record Added"
            );
            this.onClose();
            EventBus.$emit("reload-chart");
            EventBus.$emit("reload-dialog", this.parentID);
          })
          .catch((error) => {
            this.addErrorMessage(
              error.data.message ||
                "An error occurred while submitting the form"
            );
          })
          .finally(() => {
            this.loading = false; // Stop loading
          });
      }
    },
  },
};
</script>
